import React from 'react';
import useAfterUpdate from '@betgames/bg-tools/dist/hooks/useAfterUpdate';
import useImmutableState from '@betgames/bg-tools/dist/hooks/useImmutableState';
import roundToSeconds from '@betgames/bg-tools/dist/utils/roundToSeconds';
import { ClockIcon } from 'twain-sport-fe-mono/icons';
import { DateTime } from 'twain-sport-fe-mono/enums';
import { EMPTY_SPACE } from '../../constants/textSymbols';
import { Timer } from '../Timer';
import classes from './VideoTimer.module.scss';

type ITimerProps = React.ComponentProps<typeof Timer>;

interface IProps {
    until: ITimerProps['until']; // until as timestamp
    displayProgress?: boolean;
    onFinish?: ITimerProps['onFinish'];
    displayTimerIn?: number;
    prefix?: string;
    textOnFinish?: ITimerProps['textOnFinish'];
}

interface IState {
    timestamp: number;
    animateProgress: boolean;
    showDurationUnits: boolean;
}

const USE_LONG_FORMAT_AFTER_SECONDS = 60 * 60; // 60 min
const DISPLAY_TIMER_IN = 45 * 60; // 45 min
const DISPLAY_PROGRESS_IN = 7 * 60; // 7 min

const getUntil = (until: number, timestamp: number) =>
    Math.max(roundToSeconds(until) - roundToSeconds(timestamp), 0);

export const VideoTimer: React.FC<IProps> = (props: IProps) => {
    const [state, dispatch] = useImmutableState<IState>(() => {
        const timestamp = Date.now();
        return {
            timestamp,
            animateProgress: false,
            showDurationUnits: getUntil(props.until, timestamp) >= USE_LONG_FORMAT_AFTER_SECONDS,
        };
    });

    const displayTimerIn = props?.displayTimerIn || DISPLAY_TIMER_IN;

    useAfterUpdate(() => {
        if (props.until) {
            return;
        }

        dispatch((draft) => {
            draft.timestamp = Date.now();

            if (getUntil(props.until, draft.timestamp) > DISPLAY_PROGRESS_IN) {
                draft.animateProgress = false;
            }
        });
    }, [props.until]);

    const data = React.useMemo(() => {
        const until = getUntil(props.until, state.timestamp);
        const showTimer = until > 0 && until <= displayTimerIn;
        const progress = `${
            (Math.max(DISPLAY_PROGRESS_IN - until, 0) / DISPLAY_PROGRESS_IN) * 100
        }%`;

        return {
            until,
            showTimer,
            progress,
        };
    }, [state.timestamp]);

    React.useEffect(() => {
        if (!data.until || data.showTimer) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            dispatch((draft) => {
                draft.timestamp = Date.now();
            });
        }, (data.until - displayTimerIn) * 1000);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [data.showTimer]);

    const progressStyles: React.CSSProperties = React.useMemo(
        () =>
            state.animateProgress
                ? { width: data.progress, animationDuration: `${data.until}s` }
                : undefined,
        [state.animateProgress, data.until],
    );

    const onAfterExactSecond: ITimerProps['onAfterExactSecond'] = React.useMemo(() => {
        if (state.showDurationUnits) {
            return {
                second: USE_LONG_FORMAT_AFTER_SECONDS - 1,
                callback: () => {
                    dispatch((draft) => {
                        draft.timestamp = Date.now();
                        draft.showDurationUnits = false;
                    });
                },
            };
        }

        return {
            second: DISPLAY_PROGRESS_IN,
            callback: () => {
                dispatch((draft) => {
                    draft.animateProgress = true;
                });
            },
        };
    }, [state.showDurationUnits]);

    const format = React.useMemo(() => {
        if (state.showDurationUnits) {
            return DateTime.TIMER_LONG_FORMAT;
        }

        return DateTime.DEFAULT_TIMER_FORMAT;
    }, [state.showDurationUnits]);

    return (
        <>
            <ClockIcon className={classes.clockIcon} />
            {props.displayProgress && (
                <div className={classes.indicator}>
                    <div className={classes.progress} style={progressStyles} key={data.until} />
                </div>
            )}
            <div className={classes.timerText}>
                {!!props.prefix && `${props.prefix}${EMPTY_SPACE}`}
                <span className={classes.timerValue}>
                    <Timer
                        key={Number(state.showDurationUnits)}
                        timestamp={state.timestamp}
                        until={data.until}
                        format={format}
                        textOnFinish={props.textOnFinish}
                        showDurationUnits={state.showDurationUnits}
                        onAfterExactSecond={onAfterExactSecond}
                        onFinish={() => {
                            if (props.onFinish) {
                                props.onFinish();
                            }
                            dispatch((draft) => {
                                draft.timestamp = Date.now();
                                draft.animateProgress = true;
                            });
                        }}
                    />
                </span>
            </div>
        </>
    );
};
